import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import type { AnchorHTMLAttributes, FocusEvent, MouseEvent } from "react";
import { useRouter as useTanstackRouter } from "@tanstack/react-router";
import { resolveHref } from "./url";
import { toPublicHref } from "./publicHref";
import type { Url } from "./url";

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: Url;
  replace?: boolean;
  scroll?: boolean;
  /** false desliga o prefetch do chunk da página ao parar o mouse/focar no link. */
  prefetch?: boolean;
  shallow?: boolean;
};

const EXTERNAL = /^[a-z][a-z\d+.-]*:/i;

/** Tempo parado sobre o link que conta como intenção (passar pelo menu não dispara). */
const PREFETCH_INTENT_DELAY_MS = 120;

/** Respeita a "economia de dados" do navegador. */
function prefersReducedData(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData === true;
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    href,
    replace = false,
    scroll = true,
    prefetch = true,
    shallow: _shallow,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    target,
    ...rest
  },
  ref,
) {
  const router = useTanstackRouter();
  const resolved = resolveHref(href);
  const external = EXTERNAL.test(resolved);
  // O href do <a> (abrir em nova aba, copiar o link) leva a base da organização; o
  // navigate do clique recebe o caminho interno e o router acrescenta a base sozinho.
  const publicHref = useMemo(() => (external ? resolved : toPublicHref(resolved)), [external, resolved]);
  const intentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPreload = () => {
    if (intentTimer.current !== null) clearTimeout(intentTimer.current);
    intentTimer.current = null;
  };

  useEffect(() => cancelPreload, []);

  // Intenção de navegar (mouse parado ou foco no link): baixa o chunk da página
  // antes do clique. Só passar o mouse pelo menu cancela antes de disparar.
  const schedulePreload = () => {
    if (!router || !prefetch || external || prefersReducedData()) return;
    cancelPreload();
    intentTimer.current = setTimeout(() => {
      intentTimer.current = null;
      const { pathname } = new URL(resolved, window.location.origin);
      void router.preloadRoute({ to: pathname }).catch(() => undefined);
    }, PREFETCH_INTENT_DELAY_MS);
  };

  const handleMouseEnter = (event: MouseEvent<HTMLAnchorElement>) => {
    onMouseEnter?.(event);
    schedulePreload();
  };

  const handleMouseLeave = (event: MouseEvent<HTMLAnchorElement>) => {
    onMouseLeave?.(event);
    cancelPreload();
  };

  const handleFocus = (event: FocusEvent<HTMLAnchorElement>) => {
    onFocus?.(event);
    schedulePreload();
  };

  const handleBlur = (event: FocusEvent<HTMLAnchorElement>) => {
    onBlur?.(event);
    cancelPreload();
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    cancelPreload();
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    // Sem router no contexto (ex.: testes isolados), cai na navegação nativa do <a>.
    if (!router || event.defaultPrevented || modified || external || (target && target !== "_self")) return;
    event.preventDefault();
    void router.navigate({ href: resolved, replace, resetScroll: scroll });
  };

  return (
    <a
      ref={ref}
      href={publicHref}
      target={target}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...rest}
    />
  );
});

export default Link;
